import React, { useEffect, useState } from "react";
import {
  EditorState,
  convertFromHTML as convertFromHTMLDraft,
  ContentState,
} from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { Label, Colors } from "@sellout/ui";
import styled from "styled-components";
import { media } from "@sellout/ui/build/utils/MediaQuery";
import { convertToHTML } from "draft-convert";

type ContainerProps = {
  margin?: string;
};

const Container = styled.div<ContainerProps>`
  border: 1px solid ${Colors.Grey5};
  border-radius: 10px;
  background: ${Colors.White};
  margin: ${(props) => props.margin};
  min-height: 180px;
  ${media.mobile`
    max-width:350px;
  `};
`;

// TODO Safe sanitizer using DOMParser to strip <figure> */
const sanitizeHtml = (html: string) => {
  if (!html) return "";
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  // remove all <figure> nodes
  doc.querySelectorAll("figure").forEach((el) => el.remove());

  doc.querySelectorAll("p").forEach((p) => {
    if (!p.textContent?.trim()) p.remove();
  });

  return doc.body.innerHTML;
};

type RichTextEditorProps = {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  margin?: string;
  subLabel?: string;
  tip?: string;
  placeholder?: string;
};

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  label,
  margin,
  subLabel,
  tip,
  placeholder,
}) => {

const onEditorStateChange = (next: EditorState) => {
  setEditorState(next);
  const contentState = next.getCurrentContent();
  const html = convertToHTML(contentState);
  onChange(html);
};

  const isEditorEmpty = () => {
    const contentState = editorState.getCurrentContent();
    return (
      contentState.getBlockMap().size === 1 &&
      contentState.getFirstBlock().getType() === "unstyled" &&
      contentState.getFirstBlock().getText() === ""
    );
  };
const [editorState, setEditorState] = useState(() => {
  if (!value) return EditorState.createEmpty();
  const safeHtml = sanitizeHtml(value);
  const blocksFromHTML = convertFromHTMLDraft(safeHtml);
  const contentState = ContentState.createFromBlockArray(
    blocksFromHTML.contentBlocks,
    blocksFromHTML.entityMap
  );
  return EditorState.createWithContent(contentState);
});

useEffect(() => {
  if (value === "") {
    setEditorState(EditorState.createEmpty());
    return;
  }

  const contentState = editorState.getCurrentContent();
  const currentHtml = convertToHTML(contentState);

  if (value !== currentHtml) {
    const safeHtml = sanitizeHtml(value);
    const blocksFromHTML = convertFromHTMLDraft(safeHtml);
    const newContentState = ContentState.createFromBlockArray(
      blocksFromHTML.contentBlocks,
      blocksFromHTML.entityMap
    );
    setEditorState(EditorState.createWithContent(newContentState));
  }
}, [value]);

const handleKeyCommand = (command: string, state: EditorState) => {
  if (command === "backspace") {
    const selection = state.getSelection();
    if (selection.isCollapsed() && selection.getStartOffset() === 0) {
      return "handled";
    }
  }
  return "not-handled";
};

  return (
    <>
      {label && <Label text={label} subText={subLabel} tip={tip} />}
      <Container margin={margin}>
        <Editor
          editorState={editorState}
          toolbar={{
            options: ["inline", "list"],
            inline: {
              options: ["bold", "italic", "underline"],
            },
            list: {
              options: ["unordered", "ordered"],
            },
          }}
          onEditorStateChange={onEditorStateChange}
          placeholder={isEditorEmpty() ? placeholder : ""}
          spellCheck
           handleKeyCommand={handleKeyCommand}
        />
      </Container>
    </>
  );
};

export default RichTextEditor;
